
import { Component, OnInit } from '@angular/core';
import { TradingDataService, TradingPnlDto } from '../../../../services/tradingdata.service';

interface DailyPnlSummary {
  tradeDate: string;        // yyyy-MM-dd
  netCashFlow: number;
  totalFees: number;
  totalBuyNotional: number;
  totalSellNotional: number;
}

interface ProductPnlSummary {
  productId: string;
  baseCurrency: string;
  quoteCurrency: string;
  netCashFlow: number;
  totalFees: number;
  totalBuyNotional: number;
  totalSellNotional: number;
}

interface ExchangePnlSummary {
  exchange: string;
  netCashFlow: number;
  totalFees: number;
  totalBuyNotional: number;
  totalSellNotional: number;
}

@Component({
  selector: 'app-trading-dashboard-realized-pnl',
  templateUrl: './trading-dashboard-realized-pnl.component.html',
  styleUrls: ['./trading-dashboard-realized-pnl.component.css']
})
export class TradingDashboardRealizedPnlComponent implements OnInit {
  startDate!: string;
  endDate!: string;
  exchange: string = '';
  productId: string = '';

  pnlRows: TradingPnlDto[] = [];
  dailySummary: DailyPnlSummary[] = [];
  productSummary: ProductPnlSummary[] = [];
  exchangeSummary: ExchangePnlSummary[] = [];

  // ready for charts later
  netCashflowDates: string[] = [];
  netCashflowSeries: number[] = [];

  loading = false;
  error: string | null = null;

  token!: string | null;

  constructor(private tradingDataService: TradingDataService) {}

  ngOnInit(): void {
    // default dates
    const defaultStart = new Date(2024, 0, 1);
    const today = new Date();

    this.startDate = defaultStart.toISOString().substring(0, 10);
    this.endDate = today.toISOString().substring(0, 10);

    // 🔑 get token the same way as WelaraAcademy
    const userStr = sessionStorage.getItem('wOSUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.token = user.stsTokenManager?.accessToken || null;
      } catch (e) {
        console.error('Error parsing wOSUser from sessionStorage', e);
        this.token = null;
      }
    } else {
      this.token = null;
    }

    // only auto-load if we actually have a token
    if (this.token) {
      this.loadPnl();
    }
  }

  get netTotal(): number {
    return this.pnlRows.reduce(
      (sum: number, row: TradingPnlDto) => sum + row.netCashFlow,
      0
    );
  }

  async loadPnl(): Promise<void> {
    // if for some reason we don't have a token, don't scream at the user;
    // just quietly no-op
    if (!this.token) {
      console.warn('No token found in sessionStorage; skipping P&L load.');
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      const raw = await this.tradingDataService.getTradingPnlByDate(
        this.token,
        start,
        end,
        this.exchange,
        this.productId
      );

      // map PascalCase → camelCase
      const mapped: TradingPnlDto[] = (raw as any[]).map((r): TradingPnlDto => ({
        tradeDate: r.TradeDate,
        exchange: r.Exchange,
        productId: r.ProductId,
        baseCurrency: r.BaseCurrency,
        quoteCurrency: r.QuoteCurrency,
        totalBuySize: r.TotalBuySize,
        totalSellSize: r.TotalSellSize,
        totalBuyNotional: r.TotalBuyNotional,
        totalSellNotional: r.TotalSellNotional,
        totalFees: r.TotalFees,
        netCashFlow: r.NetCashFlow
      }));

      this.pnlRows = mapped;
      this.buildSummaries();
    } catch (err: any) {
      console.error('Error loading PnL:', err);
      this.error = err?.message || 'Failed to load PnL data.';
    } finally {
      this.loading = false;
    }
  }

  private buildSummaries(): void {
    const byDate = new Map<string, DailyPnlSummary>();
    const byProduct = new Map<string, ProductPnlSummary>();
    const byExchange = new Map<string, ExchangePnlSummary>();

    for (const row of this.pnlRows) {
      const dateKey = new Date(row.tradeDate).toISOString().substring(0, 10);

      // by date
      let d = byDate.get(dateKey);
      if (!d) {
        d = {
          tradeDate: dateKey,
          netCashFlow: 0,
          totalFees: 0,
          totalBuyNotional: 0,
          totalSellNotional: 0
        };
        byDate.set(dateKey, d);
      }
      d.netCashFlow += row.netCashFlow;
      d.totalFees += row.totalFees;
      d.totalBuyNotional += row.totalBuyNotional;
      d.totalSellNotional += row.totalSellNotional;

      // by product
      const prodKey = row.productId;
      let p = byProduct.get(prodKey);
      if (!p) {
        p = {
          productId: row.productId,
          baseCurrency: row.baseCurrency,
          quoteCurrency: row.quoteCurrency,
          netCashFlow: 0,
          totalFees: 0,
          totalBuyNotional: 0,
          totalSellNotional: 0
        };
        byProduct.set(prodKey, p);
      }
      p.netCashFlow += row.netCashFlow;
      p.totalFees += row.totalFees;
      p.totalBuyNotional += row.totalBuyNotional;
      p.totalSellNotional += row.totalSellNotional;

      // by exchange
      const exKey = row.exchange;
      let e = byExchange.get(exKey);
      if (!e) {
        e = {
          exchange: row.exchange,
          netCashFlow: 0,
          totalFees: 0,
          totalBuyNotional: 0,
          totalSellNotional: 0
        };
        byExchange.set(exKey, e);
      }
      e.netCashFlow += row.netCashFlow;
      e.totalFees += row.totalFees;
      e.totalBuyNotional += row.totalBuyNotional;
      e.totalSellNotional += row.totalSellNotional;
    }

    this.dailySummary = Array.from(byDate.values())
      .sort((a, b) => a.tradeDate.localeCompare(b.tradeDate));

    this.productSummary = Array.from(byProduct.values())
      .sort((a, b) => a.productId.localeCompare(b.productId));

    this.exchangeSummary = Array.from(byExchange.values())
      .sort((a, b) => a.exchange.localeCompare(b.exchange));

    this.netCashflowDates = this.dailySummary.map(d => d.tradeDate);
    this.netCashflowSeries = this.dailySummary.map(d => d.netCashFlow);
  }
}
